import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components/ui';
import { i18n, type MessageKey } from 'shared/i18n';
import { RegionRow, TableRow } from 'app/api/coverage-types';

interface Props {
  regions: RegionRow[];
  tables: TableRow[];
  onSelect: (row: RegionRow | TableRow) => void;
  regionsEmpty: boolean;
  tablesEmpty: boolean;
  allEmpty: boolean;
  regionsPagination?: React.ReactNode;
  tablesPagination?: React.ReactNode;
}

const statusLabelKey = (s: RegionRow['status']): MessageKey => `tag:coverage.status.${s}`;
const ruleLabelKey = (k: string) => `tag:coverage.rule.${k === 'all-cells' ? 'allCells' : k}.short` as MessageKey;

export const CoverageOverviewTable = ({ regions, tables, onSelect, regionsEmpty, tablesEmpty, allEmpty, regionsPagination, tablesPagination }: Props) => {
  const { t } = useTranslation('tag');

  if (allEmpty) {
    return (
      <div className="coverage-overview-table__empty-all">
        <Typography variant="body-5">{t('coverage.overview.emptyState')}</Typography>
      </div>
    );
  }

  return (
    <div className="coverage-overview-table">
      <section>
        <header className="coverage-overview-table__section-header">
          <Typography variant="body-5" fontWeight="bold">
            {t('coverage.section.regions')}
          </Typography>
        </header>
        {regionsEmpty ? (
          <div className="coverage-overview-table__empty">
            <Typography variant="body-5">{t('coverage.section.regionsEmpty')}</Typography>
          </div>
        ) : (
          <>
            <table className="coverage-overview-table__rows">
              <thead>
                <tr>
                  <th>{t('coverage.col.anchor')}</th>
                  <th>{t('coverage.col.section')}</th>
                  <th>{t('coverage.col.page')}</th>
                  <th>{t('coverage.col.rule')}</th>
                  <th>{t('coverage.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={`${r.sectionSuperId}-${r.anchorPath.join('.')}`} onClick={() => onSelect(r)} className="coverage-overview-table__row">
                    <td>{r.heading || t('coverage.heading.fallback')}</td>
                    <td>{r.sectionName || r.sectionSuperId}</td>
                    <td>{r.page}</td>
                    <td>
                      <span className={`coverage-rule-badge coverage-rule-badge--${r.rule.kind}`}>{i18n(ruleLabelKey(r.rule.kind))}</span>
                    </td>
                    <td>
                      <span className={`coverage-status-badge coverage-status-badge--${r.status}`}>{i18n(statusLabelKey(r.status))}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {regionsPagination && <div className="coverage-overview-table__pagination">{regionsPagination}</div>}
          </>
        )}
      </section>

      <section>
        <header className="coverage-overview-table__section-header">
          <Typography variant="body-5" fontWeight="bold">
            {t('coverage.section.tables')}
          </Typography>
        </header>
        {tablesEmpty ? (
          <div className="coverage-overview-table__empty">
            <Typography variant="body-5">{t('coverage.section.tablesEmpty')}</Typography>
          </div>
        ) : (
          <>
            <table className="coverage-overview-table__rows">
              <thead>
                <tr>
                  <th>{t('coverage.col.table')}</th>
                  <th>{t('coverage.col.section')}</th>
                  <th>{t('coverage.col.page')}</th>
                  <th>{t('coverage.col.status')}</th>
                  <th>{t('coverage.col.missingCells')}</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((table) => (
                  <tr key={`${table.sectionSuperId}-${table.tableNodePath.join('.')}`} onClick={() => onSelect(table)} className="coverage-overview-table__row">
                    <td>{table.tableName || t('coverage.table.fallbackName')}</td>
                    <td>{table.sectionName || table.sectionSuperId}</td>
                    <td>{table.page}</td>
                    <td>
                      <span className={`coverage-status-badge coverage-status-badge--${table.status}`}>{i18n(statusLabelKey(table.status))}</span>
                    </td>
                    <td>{table.missingCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tablesPagination && <div className="coverage-overview-table__pagination">{tablesPagination}</div>}
          </>
        )}
      </section>
    </div>
  );
};
